import { useRequiredContext } from "@redotech/react-util/context";
import {
  LoyaltyEarningRule,
  LoyaltyEarningRuleType,
} from "@redotech/redo-model/loyalty/loyalty-definition";
import { EarningRulesBlock } from "@redotech/redo-model/loyalty/loyalty-page-builder/definition";
import { JSXElementConstructor, memo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "../../arbiter-components/featured-icon/redo-featured-icon";
import ChevronDown from "../../arbiter-icon/chevron-down.svg";
import ChevronUp from "../../arbiter-icon/chevron-up.svg";
import GiftIcon from "../../arbiter-icon/grid-01.svg";
import HeartIcon from "../../arbiter-icon/heart.svg";
import StarIcon from "../../arbiter-icon/star-01.svg";
import UserIcon from "../../arbiter-icon/user-01.svg";
import { Divider } from "../../divider";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { LoyaltyPageContext } from "../context/loyalty-page-context";

export const EarningRules = memo(function EarningRules({
  title,
  titleColor,
  subtitle,
  subtitleColor,
}: EarningRulesBlock) {
  const context = useRequiredContext(LoyaltyPageContext);
  const loyaltyEarningRules = context.loyaltyEarningRules.filter(
    (earningRule) => earningRule.enabled,
  );

  // TODO: aradford - check if customer has completed any earning rules

  return (
    <Flex dir="column" gap="3xl" px="xl" py="6xl" w="full">
      <Text
        fontSize="2xl"
        fontWeight="bold"
        style={{ color: titleColor || "#000000" }}
      >
        {title || "Ways to Earn Points"}
      </Text>

      <Text fontSize="xs" style={{ color: subtitleColor }}>
        {subtitle ||
          "Complete these activities to earn more points and unlock rewards"}
      </Text>

      <Flex dir="column" gap="3xl" w="full">
        {loyaltyEarningRules.length === 0 ? (
          <Text fontSize="lg" fontStyle="italic">
            You've completed all earning activities
          </Text>
        ) : (
          loyaltyEarningRules.map((earningRule) => (
            <EarningRuleTile earningRule={earningRule} key={earningRule.type} />
          ))
        )}
      </Flex>
    </Flex>
  );
});

const EarningRuleTile = memo(function EarningRuleTile({
  earningRule,
}: {
  earningRule: LoyaltyEarningRule;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const { Icon, title, description, expandedDescription } =
    earningRuleTileMap[earningRule.type];

  return (
    <Flex dir="column" gap="none">
      <Flex
        bgColor={isHovering ? "tertiary" : "primary"}
        dir="row"
        gap="3xl"
        justify="space-between"
        onClick={() => setIsExpanded(!isExpanded)}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        p="lg"
        radius="sm"
        w="full"
      >
        <Flex align="center" dir="row" gap="lg">
          <RedoFeaturedIcon color="gray" Icon={Icon} size="xs" />
          <Flex dir="column" gap="none">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {title}
            </Text>
            <Text fontSize="xs" fontWeight="thin" textColor="tertiary">
              {description}
            </Text>
          </Flex>
        </Flex>
        <RedoButton
          IconLeading={isExpanded ? ChevronUp : ChevronDown}
          onClick={() => setIsExpanded(!isExpanded)}
        />
      </Flex>
      {isExpanded && (
        <Flex dir="column" p="lg">
          <Divider />
          <Text fontSize="xs" fontWeight="thin" textColor="tertiary">
            {expandedDescription}
          </Text>
        </Flex>
      )}
    </Flex>
  );
});

const earningRuleTileMap: Record<
  LoyaltyEarningRuleType,
  {
    Icon: JSXElementConstructor<any>;
    title: string;
    description: string;
    expandedDescription: string;
  }
> = {
  [LoyaltyEarningRuleType.PURCHASE]: {
    Icon: GiftIcon,
    title: "Make a purchase",
    description: "Points earned per dollar spent",
    expandedDescription:
      "Earn 1 point for every dollar you spend on any purchase. This rule is always active.",
  },
  [LoyaltyEarningRuleType.ACCOUNT_CREATION]: {
    Icon: UserIcon,
    title: "Create an account",
    description: "Points awarded upon account creation",
    expandedDescription: "Create an account to earn points.",
  },
  [LoyaltyEarningRuleType.REVIEW]: {
    Icon: StarIcon,
    title: "Write a review",
    description: "Points earned for writing a product review",
    expandedDescription:
      "Write a review for any product you've purchased to earn points.",
  },
  [LoyaltyEarningRuleType.INSTAGRAM]: {
    Icon: HeartIcon,
    title: "Follow on Instagram",
    description: "Points earned for following on Instagram",
    expandedDescription: "",
  },
  [LoyaltyEarningRuleType.FACEBOOK]: {
    Icon: HeartIcon,
    title: "Like on Facebook",
    description: "Points earned for liking on Facebook",
    expandedDescription:
      "Like our Facebook page to join our community and earn points. Click the link below to visit our Facebook page.",
  },
  [LoyaltyEarningRuleType.TIKTOK]: {
    Icon: HeartIcon,
    title: "Follow on TikTok",
    description: "Points earned for following on TikTok",
    expandedDescription:
      "Follow our TikTok page to join our community and earn points. Click the link below to visit our TikTok page.",
  },
  [LoyaltyEarningRuleType.BIRTHDAY]: {
    Icon: HeartIcon,
    title: "Birthday",
    description: "Points awarded on your birthday",
    expandedDescription:
      "Set your birthday below to receive special bonus points on your special day each year.",
  },
};
